import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { notification } from "antd";
import { useIntl, FormattedMessage } from "react-intl";
import { Button } from "@ts-digital/vrc";
import { StandardTabs, Tab } from "@ts-digital/vrc";
import RegistryForm from "./registryForm";
import { useDispatch, useSelector } from "react-redux";
import { createParty, getParties } from "../../../store/actions/parties";
import PaymentForm from "./paymentForm";
import LoaderSimple from "../../general/loader";
import isoCountriesLanguages from "iso-countries-languages";
import iban from "iban";

import { useIsButtonRegistryActive } from "../../../customHooks/useIsButtonRegistryActive";

const CreateNewContactPage = ({ registryStore, isPage }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState(0);
  const [readyToSaveContact, setReadyToSaveContact] = useState(false);
  const [installmentsRounded, setInstallmentsRounded] = useState([]);
  const [paymentFrequency, setPaymentFrequency] = useState("single");
  const mounted = useRef();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const main = useSelector(state => state.main);
  const isSavingState = useSelector(state => state.parties.createParty.status);
  const isSavingSuccess =
    isSavingState?.started === false &&
    isSavingState?.ended === true &&
    isSavingState?.error === false;

  const isSavingError =
    isSavingState?.started === false &&
    isSavingState?.ended === false &&
    isSavingState?.error === true;

  const isLoadingSaving =
    isSavingState?.started === true &&
    isSavingState?.error === false &&
    isSavingState?.ended === false;

  useEffect(() => {    
    if (readyToSaveContact) {
      dispatch(createParty(
        auth, main.companyUuidSelected, registryStore.contactBody
      ))
        .then(res => registryStore.setFullScreenCreateNew(false))
        .then(res => {
          if (isPage) {
            navigate("/parties");
          }
          dispatch(getParties(
            auth, main.companyUuidSelected, 100, "asc"
          )).then(res => registryStore.setIsLastContactAdded(!registryStore.isLastContactAdded));
        });
    }
    setReadyToSaveContact(false);
    registryStore.setInstallments([
      {
        dueDate: { number: 30, interval: "giorni", from: "month-end" },
        amount: 100,
        iban: "",
        paymentMethod: "MP05"
      }
    ]);

    registryStore.setContactBody({
      type: "COMPANY",
      relationships: ["CUSTOMER"],
      iban: "",
      title: "",
      name: "",
      bankAccounts: [],
      legalNature: "",
      taxRepresentative: {}
    });

    registryStore.setVatNumber("");
    registryStore.setTaxId("");
    registryStore.setEoriNumber("");
    registryStore.setCountry("");
    registryStore.setEmailAddress("");
    registryStore.setPecAddress("");
    registryStore.setAddress({
      region: "",
      country: "",
      municipality: "",
      cap: "",
      province: "",
      street: "",
      number: ""
    });
    registryStore.setPaymentDetails({ iban: "" });
    registryStore.setMunicipalityError(null);
    registryStore.setCapError(null);
    registryStore.setProvinceError(null);
    registryStore.setEmailError(null);
    registryStore.setIbanError(null);
    registryStore.setTaxIdError(null);
    registryStore.setVatError(null);
  }, [readyToSaveContact]);

  useEffect(() => {
    const amounts = registryStore.installments?.map(i => Math.floor(i.amount));
    const sum = amounts?.reduce((a, b) => a + b, 0);
    const difference = 100 - sum;
    const calcRoundedAm = (i, index) => {
      if (sum < 100) {
        if (index === registryStore.installments?.length - 1) {
          return Math.floor(i.amount + difference);
        } 
        return Math.floor(i.amount);
        
      } else if (sum > 100) {
        if (index === registryStore.installments?.length - 1) {
          return Math.floor(i.amount + difference);
        } 
        return Math.floor(i.amount);
        
      } 
      return Math.floor(i.amount);
      
    };
    const installmentsRoundedAm = registryStore.installments?.map((i, index) => {
      return {
        ...i,
        amount: calcRoundedAm(i, index)
      };
    });

    setInstallmentsRounded([...installmentsRoundedAm]);
  }, [registryStore.installments]);

  useEffect(() => {
    const countriesInItalian = Object.values(isoCountriesLanguages.getCountries("it"));
    registryStore.setCountries(countriesInItalian);
  }, []);

  useEffect(() => {
    onBlurTaxIdValue(registryStore.taxId, registryStore.country);
    onBlurVatNumberValue(registryStore.vatNumber, registryStore.country);
  }, [registryStore.country]);

  useEffect(() => {
    if (!mounted.current) {
      // set mounted to true
      mounted.current = true;
    } else {
      // do componentDidUpdate logic
      const type = isSavingSuccess ? "success" : isSavingError ? "error" : "";
      if (type !== "") {
        openSaveNotification(type);
      }
    }
  }, [isSavingSuccess, isSavingError]);

  const cfpiva = require("cfpiva");

  const onChangeIban = e => {
    const isValidIban = iban.isValid(e.target.value);
    registryStore.setIbanError(isValidIban ? false : true);
  };

  const onBlurTaxId = e => {
    if (registryStore.country === "Italia") {
      let isValidTaxId;
      let isValidVatNumber;
      if (e.target !== undefined) {
        isValidTaxId = cfpiva.controllaCF(e.target.value);
        isValidVatNumber = cfpiva.controllaPIVA(e.target.value);
      } else {
        isValidTaxId = cfpiva.controllaCF(e);
        isValidVatNumber = cfpiva.controllaPIVA(e);
      }
      if (registryStore.contactBody.type === "PERSON") {
        registryStore.setTaxIdError(isValidTaxId ? false : true);
      }
      if (registryStore.contactBody.type === "COMPANY") {
        registryStore.setTaxIdError(isValidTaxId || isValidVatNumber ? false : true);
      }
    }
  };

  const onBlurVatNumber = e => {
    if (registryStore.country === "Italia") {
      const isValidVatNumber = cfpiva.controllaPIVA(e.target.value);
      registryStore.setVatError(isValidVatNumber ? false : true);
    }
  };

  const onBlurTaxIdValue = (taxId, country) => {
    if (taxId.length > 0 && country === "Italia") {
      const isValidTaxId = cfpiva.controllaCF(taxId);
      registryStore.setTaxIdError(isValidTaxId ? false : true);
    } else {
      registryStore.setTaxIdError(null);
    }
  };

  const onBlurVatNumberValue = (vatNumber, country) => {
    if (vatNumber.length > 0 && country === "Italia") {
      const isValidVatNumber = cfpiva.controllaPIVA(vatNumber);
      registryStore.setVatError(isValidVatNumber ? false : true);
    } else {
      registryStore.setVatError(null);
    }
  };

  const changePaymentFrequency = selectedValue =>
    setPaymentFrequency(selectedValue);

  const buttonActiveConditions =
    registryStore.contactBody.type === "PERSON"
      ? registryStore.country?.length > 0 &&
        registryStore.address?.country.length > 0 &&
        registryStore.address?.municipality.length > 0 &&
        registryStore.address?.cap.length > 0 &&
        registryStore.address?.province.length > 0 &&
        registryStore.address?.street.length > 0 &&
        registryStore.address?.number.length > 0 &&
        registryStore.emailAddress?.length > 0 &&
        registryStore.contactBody?.firstName?.length > 0 &&
        registryStore.contactBody?.lastName?.length > 0 &&
        registryStore.contactBody?.relationships?.length > 0 &&
        !registryStore.municipalityError &&
        !registryStore.provinceError &&
        !registryStore.capError &&
        !registryStore.emailError
      : registryStore.country?.length > 0 &&
        registryStore.address?.country.length > 0 &&
        registryStore.address?.municipality.length > 0 &&
        registryStore.address?.cap.length > 0 &&
        registryStore.address?.province.length > 0 &&
        registryStore.address?.street.length > 0 &&
        registryStore.address?.number.length > 0 &&
        registryStore.emailAddress?.length > 0 &&
        registryStore.contactBody?.relationships?.length > 0 &&
        !registryStore.municipalityError &&
        !registryStore.provinceError &&
        !registryStore.capError &&
        !registryStore.emailError;

  const isButtonActive = useIsButtonRegistryActive(
    buttonActiveConditions,
    registryStore.contactBody,
    registryStore.pecAddress,
    registryStore.installments,
    registryStore.taxId,
    registryStore.vatNumber,
    registryStore.ibanError,
    registryStore.pecError,
    registryStore.installmentsError,
    registryStore.taxIdError,
    registryStore.vatError
  );

  const feedbackMessageSuccess = useIntl().formatMessage({
    id: "parties.create.feedbackMessageSuccess"
  });
  const feedbackMessageError = useIntl().formatMessage({
    id: "parties.create.feedbackMessageError"
  });
  const feedbackDescriptionError = useIntl().formatMessage({
    id: "parties.create.feedbackDescriptionError"
  });

  const installmentsToSave = installmentsRounded?.map(i => {
    return {
      paymentMethods: [i.paymentMethod],
      fractionOver10K: i.amount * 100,
      dueDate: {
        days:
          i.dueDate?.interval === "giorni"
            ? i.dueDate?.number
            : i.dueDate?.number * 30,
        from:
          i.dueDate?.from === "month-end"
            ? "DOCUMENT_DATE_END_OF_MONTH"
            : "DOCUMENT_DATE"
      }
    };
  });

  const openSaveNotification = type => {
    notification[type]({
      message: isSavingSuccess ? feedbackMessageSuccess : feedbackMessageError,
      description: isSavingError ? feedbackDescriptionError : "",
      className: isSavingSuccess ? "success-create-party" : "error-create-party"
    });
  };

  const saveNewContact = () => {
    let identifiers = [];
    let emailAddresses = [];
    if (registryStore.vatNumber) {
      identifiers.push({
        type: "VAT_NUMBER",
        country: registryStore.country,
        value: registryStore.vatNumber
      });
    }
    if (registryStore.taxId) {
      identifiers.push({
        type: "TAX_ID",
        country: registryStore.country,
        value: registryStore.taxId
      });
    }
    if (registryStore.eoriNumber) {
      identifiers.push({
        type: "EORI_NUMBER",
        country: registryStore.country,
        value: registryStore.eoriNumber
      });
    }
    if (registryStore.emailAddress) {
      emailAddresses.push({
        description: "",
        type: "STANDARD",
        value: registryStore.emailAddress,
        scopes: []
      });
    }
    if (registryStore.pecAddress) {
      emailAddresses.push({
        description: "",
        type: "PEC",
        value: registryStore.pecAddress,
        scopes: []
      });
    }
    registryStore.setContactBody({
      ...registryStore.contactBody,
      taxId: registryStore.taxId,
      vatNumber: registryStore.vatNumber,
      identifiers: identifiers,
      emailAddresses: emailAddresses,
      places: [
        {
          description: "",
          address: {
            country: registryStore.address?.country,
            region: registryStore.address?.region,
            province: registryStore.address?.province,
            municipality: registryStore.address?.municipality,
            city: null,
            street: {
              name: registryStore.address?.street,
              number: registryStore.address?.number,
              additionalInfo: registryStore.address?.additionalInfo
                ? registryStore.address?.additionalInfo
                : ""
            },
            postalCode: registryStore.address?.cap
          },
          scopes: []
        }
      ],
      bankAccounts: [
        {
          description: "",
          iban: registryStore.contactBody?.iban
            ? registryStore.contactBody?.iban
            : "",
          swiftCode: "",
          abiCode: "",
          cabCode: "",
          scopes: []
        }
      ],
      sdiId: registryStore.contactBody?.sdiId,
      taxRepresentative: {
        identifiers: [],
        name: "",
        firstName: "",
        lastName: ""
      },
      firstName: registryStore.contactBody?.firstName
        ? registryStore.contactBody?.firstName
        : "",
      lastName: registryStore.contactBody?.lastName
        ? registryStore.contactBody?.lastName
        : "",
      negotiatedPaymentPlans: [
        {
          installments: [...installmentsToSave],
          scopes: [
            registryStore.contactBody?.paymentScopes
              ? registryStore.contactBody?.paymentScopes
              : "null"
          ]
        }
      ]
    });
    setReadyToSaveContact(true);
  };

  const onCancel = () => {
    registryStore.setIbanPaymentFormError(null);
    registryStore.setMunicipalityError(null);
    registryStore.setCapError(null);
    registryStore.setProvinceError(null);
    registryStore.setEmailError(null);
    registryStore.setPecError(null);
    registryStore.setIbanError(null);
    registryStore.setInstallments([
      {
        dueDate: { number: "30", interval: "giorni", from: "month-end" },
        amount: 100,
        iban: "",
        paymentMethod: "MP05"
      }
    ]);
    navigate("/parties");
  };

  const footerConditions =
    activeTab === 1 && location.pathname === "/tspay-payment";
    
  return (
    <>
      {!isLoadingSaving ? (
        <div className="dbx-parties-root-create">
          <div className="dbx-new-contact-header">
            {/* <h4 className="dbx-new-contact-title">
          <FormattedMessage id="parties.createNew.pageTitle" />
        </h4> */}
            {/* <p className="dbx-new-contact-description"><FormattedMessage id="parties.createNew.searchDescription" /></p>
				<Input
					// value={searchValue}
					// onChange={}
					className={"dbx-new-contact-search"}
					placeholder="Cerca per ragione sociale, partita iva o codice fiscale"
					suffix={
						<Button className="dbx-new-contact-button-search" style={{ marginRight: -8 }}>
							<FontAwesomeIcon icon={["far", "search"]} />
						</Button>
					}
        /> */}
          </div>
          <StandardTabs
            style={{ marginBottom: "5px", overflow: "hidden", width: "100%" }}
            activeIndex={activeTab}
            onChange={e => setActiveTab(e)}
          >
            <Tab className="dbx-new-contact-tab" key={0}>
              <FormattedMessage id="parties.createNew.registryTabTitle" />
            </Tab>
            <Tab className="dbx-new-contact-tab" key={1}>
              <FormattedMessage id="parties.createNew.paymentPlansTabTitle" />
            </Tab>
            <Tab key={2} style={{ width: "100%" }} disabled={true}></Tab>
          </StandardTabs>
          {activeTab === 0 && (
            <div className="dbx-new-contact-form-container">
              <RegistryForm
                contactBody={registryStore.contactBody}
                setContactBody={registryStore.setContactBody}
                vatNumber={registryStore.vatNumber}
                setVatNumber={registryStore.setVatNumber}
                taxId={registryStore.taxId}
                setTaxId={registryStore.setTaxId}
                eoriNumber={registryStore.eoriNumber}
                setEoriNumber={registryStore.setEoriNumber}
                country={registryStore.country}
                setCountry={registryStore.setCountry}
                emailAddress={registryStore.emailAddress}
                setEmailAddress={registryStore.setEmailAddress}
                pecAddress={registryStore.pecAddress}
                setPecAddress={registryStore.setPecAddress}
                address={registryStore.address}
                setAddress={registryStore.setAddress}
                onBlurMunicipality={registryStore.onBlurMunicipality}
                municipalityError={registryStore.municipalityError}
                onBlurProvince={registryStore.onBlurProvince}
                provinceError={registryStore.provinceError}
                countries={registryStore.optionCountries}
                onChangeCountry={registryStore.onChangeCountry}
                onBlurCap={registryStore.onBlurCap}
                capError={registryStore.capError}
                ibanError={registryStore.ibanError}
                onChangeIban={onChangeIban}
                emailError={registryStore.emailError}
                pecError={registryStore.pecError}
                taxIdError={registryStore.taxIdError}
                vatError={registryStore.vatError}
                onBlurEmail={registryStore.onBlurEmail}
                onBlurPec={registryStore.onBlurPec}
                onBlurTaxId={onBlurTaxId}
                onBlurVatNumber={onBlurVatNumber}
                isEdit={false}
              />
            </div>
          )}
          {activeTab === 1 && (
            <div className="dbx-new-contact-form-container">
              <PaymentForm
                contactBody={registryStore.contactBody}
                setContactBody={registryStore.setContactBody}
                paymentFrequency={paymentFrequency}
                changePaymentFrequency={changePaymentFrequency}
                paymentDetails={registryStore.paymentDetails}
                setPaymentDetails={registryStore.setPaymentDetails}
                onChangeIban={registryStore.onChangeIbanPaymentForm}
                ibanError={registryStore.ibanPaymentFormError}
                installments={registryStore.installments}
                setInstallments={registryStore.setInstallments}
                installmentsError={registryStore.installmentsError}
                setInstallmentsError={registryStore.setInstallmentsError}
              />
            </div>
          )}
          <div className="dbx-new-contact-footer-container">
            <div
              className="dbx-new-contact-footer"
              style={{
                position: footerConditions && "absolute",
                bottom: footerConditions && "0"
              }}
            >
              <Button
                tabIndex={20}
                kind="secondary"
                colorScheme={"ghost"}
                style={{ marginRight: "16px" }}
                className="buttonNav"
                onClick={() => {
                  onCancel();
                }}
              >
                <FormattedMessage id="actions.cancel" />
              </Button>
              <Button
                tabIndex={19}
                kind="primary"
                style={{ marginRight: "16px" }}
                className="dbx-new-contact-save-button"
                disabled={!isButtonActive()}
                onClick={() => {
                  saveNewContact();
                  if (window.aptrinsic) {
                    window.aptrinsic("track", "digital-box:NEW-CONTACT");
                  }
                }}
              >
                <FormattedMessage id="actions.save" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="dbx-new-contact-loader-container">
          <LoaderSimple />
        </div>
      )}
    </>
  );
};

export default CreateNewContactPage;
